import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    isEqual as _isEqual,
    cloneDeep as _cloneDeep,
} from 'lodash';
import clsx from 'clsx';

import { makeStyles } from '@mui/styles';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import DeleteIcon from '@mui/icons-material/Delete';

import Push from '../../../components/push/';
import ConfirmationDialog from '../../../components/confirmationdlg';

import PolicyService from '../../../data/policy-service';
import Auth from '../../../data/auth-service';

import Animations from '../../../libraries/animations';

const useStyles = makeStyles(theme => ({
    formControl: {
        margin: theme.spacing(1),
        width: '100%',
    },
    buttonGroup: {
        '& button': {
            float: 'right',
            marginLeft: theme.spacing(1),
        },
    },
    varsInput: {
        width: '100%',
        '& .MuiInputBase-input': {
            fontSize: '0.85rem',
        }
    },
    ...Animations.Push(theme),
    warningBox: {
        border: `1px solid ${theme.palette.warning.main}`,
        background: theme.styled.paper.background,
        padding: theme.spacing(2),
    },
}));

const untouchables = ['AIM_SERVERS', 'DNP3_CLIENT', 'DNP3_SERVER', 'DNS_SERVERS', 'ENIP_CLIENT', 'ENIP_SERVER', 'EXTERNAL_NET',
    'HOME_NET', 'HTTP_SERVERS', 'MODBUS_CLIENT', 'MODBUS_SERVER', 'SMTP_SERVERS', 'SQL_SERVERS', 'TELNET_SERVERS', 'DNP3_PORTS',
    'FILE_DATA_PORTS', 'FTP_PORTS', 'HTTP_PORTS', 'MODBUS_PORTS', 'ORACLE_PORTS', 'SHELLCODE_PORTS', 'SSH_PORTS', 'VXLAN_PORTS', 'GENEVE_PORTS', 'TEREDO_PORTS', 'DC_SERVERS', ''];

const SuricataVars = () => {
    const classes = useStyles();
    const params = useParams();
    const location = useLocation();
    const [policy, setPolicy] = useState({});
    const [addressGroups, setAddressGroups] = useState([]);
    const [portGroups, setPortGroups] = useState([]);
    const varsPerms = Auth.permissions.get('SuricataVarsPolicy');
    const [readonly, setReadonly] = useState(!varsPerms.write);
    const [originalAddresses, setOriginalAddresses] = useState();
    const [originalPorts, setOriginalPorts] = useState();
    const [addressChanges, setAddressChanges] = useState(false);
    const [portChanges, setPortChanges] = useState(false);
    const [mode, setMode] = useState('suricata_config');
    const [modified, setModified] = useState();
    const [pushing, setPushing] = useState(false);

    const MC2CHECK = process.env.BRICATA_APPLIANCE_TYPE === 'mc2';

    useEffect(() => {
        getPolicy();
    }, []);

    useEffect(() => {
        setAddressChanges(!_isEqual(addressGroups, originalAddresses));
    }, [addressGroups]);

    useEffect(() => {
        setPortChanges(!_isEqual(portGroups, originalPorts));
    }, [portGroups]);

    const getPolicy = () => {
        PolicyService.getPolicy('suricata_config', params.policy).then(res => {
            setPolicy(res);
            const a = Object.keys(res.data.vars['address-groups']).map(v => {
                return {
                    key: v,
                    value: res.data.vars['address-groups'][v],
                    editable: (untouchables.indexOf(v) === -1),
                };
            });
            const p = Object.keys(res.data.vars['port-groups']).map(v => {
                return {
                    key: v,
                    value: res.data.vars['port-groups'][v],
                    editable: (untouchables.indexOf(v) === -1),
                };
            });

            setOriginalAddresses(_cloneDeep(a));
            setOriginalPorts(_cloneDeep(p));

            setAddressGroups(a);
            setPortGroups(p);

            setReadonly(readonly || (varsPerms.override ? false : (!MC2CHECK && (res.source === 'mc2' || location.pathname.includes('.mc2')))));
        }, err => {
            if (typeof err === 'string') {
                toast.error(err);
            }
        });
    };

    const handleInfo = () => {
        window.open('https://docs.suricata.io/en/suricata-7.0.14/configuration/suricata-yaml.html?#rule-vars');
    };

    const handlePushPolicy = () => {
        setMode('push');
    };

    const maybePushPolicy = () => {
        if (!checkForDuplicates()) {
            if (!checkAddedOrRemoved()) {
                handlePushPolicy();
            }
        }
    };

    // check for duplicate keys with an assist from Copilot
    const checkForDuplicates = () => {
        // check for duplicate keys
        const dups = [];
        const a = {};
        addressGroups.forEach(v => {
            if (a[v.key]) {
                dups.push(v.key);
            }
            a[v.key] = true;
        });
        const p = {};
        portGroups.forEach(v => {
            if (p[v.key]) {
                dups.push(v.key);
            }
            p[v.key] = true;
        });

        if (dups.length > 0) {
            toast.error(`Duplicate keys found: ${dups.join(', ')}`);
        }

        return dups.length > 0;
    };

    // variables that are added or removed are applied to all policies
    const checkAddedOrRemoved = () => {
        const a = addressGroups.reduce((a, c) => ({ ...a, [c.key]: c.value }), {});
        const p = portGroups.reduce((a, c) => ({ ...a, [c.key]: c.value }), {});

        const oga = originalAddresses.reduce((a, c) => ({ ...a, [c.key]: c.value }), {});
        const ogp = originalPorts.reduce((a, c) => ({ ...a, [c.key]: c.value }), {});

        // check for removed keys
        let ret = false;
        for (const k in a) {
            if (oga[k] === undefined) {
                setModified(current => {
                    current = current || { added: [], removed: [] };
                    return { ...current, added: [...current.added, k] };
                });
                ret = true;
            }
        }
        for (const k in p) {
            if (ogp[k] === undefined) {
                setModified(current => {
                    current = current || { added: [], removed: [] };
                    return { ...current, added: [...current.added, k] };
                });
                ret = true;
            }
        }

        // check for added keys
        for (const k in oga) {
            if (a[k] === undefined) {
                setModified(current => {
                    current = current || { added: [], removed: [] };
                    return { ...current, removed: [...current.removed, k] };
                });
                ret = true;
            }
        }
        for (const k in ogp) {
            if (p[k] === undefined) {
                setModified(current => {
                    current = current || { added: [], removed: [] };
                    return { ...current, removed: [...current.removed, k] };
                });
                ret = true;
            }
        }

        return ret;
    };

    const handleDeployPolicy = (sensorList) => {
        setModified(undefined);
        const p = { ...policy };

        p.data.vars['address-groups'] = addressGroups.reduce((a, c) => (a[c.key] = c.value, a), {});
        p.data.vars['port-groups'] = portGroups.reduce((a, c) => (a[c.key] = c.value, a), {});

        setPushing(true);
        PolicyService.updatePolicy('suricata_config', p.name, p).then(res => {
            getPolicy();
            toast.success('Policy saved successfully');
            setMode('suricata_config');

            if (sensorList.length || Object.keys(sensorList).length) {
                PolicyService.assignPolicy('suricata_config', params.policy, sensorList).then(() => {
                    toast.success('Policy pushed to sensor(s) successfully');
                }, err => {
                    if (typeof err === 'string') {
                        toast.error('Pushing policy to new sensor(s) failed : ' + err);
                    } else {
                        toast.error(err.message + ', ' + err.error);
                    }
                });
            }
        }, err => {
            if (err) {
                toast.error(err.message + ': ' + err.error);
            }
        }).finally(() => { setPushing(false) });
    };

    const handleRevertPolicy = () => {
        getPolicy();
    };

    const handleAddAddressGroup = () => {
        const a = [...addressGroups];
        a.push({ key: '', value: '', editable: true });
        setAddressGroups(a);
    };

    const handleAddPortGroup = () => {
        const p = [...portGroups];
        p.push({ key: '', value: '', editable: true });
        setPortGroups(p);
    };

    const handleUpdateAddressKey = (e, i) => {
        const a = [...addressGroups];
        a[i].key = e.target.value;
        setAddressGroups(a);
    };

    const handleUpdateAddressValue = (e, i) => {
        const a = [...addressGroups];
        a[i].value = e.target.value;
        setAddressGroups(a);
    };

    const handleUpdatePortKey = (e, i) => {
        const p = [...portGroups];
        p[i].key = e.target.value;
        setPortGroups(p);
    };

    const handleUpdatePortValue = (e, i) => {
        const p = [...portGroups];
        p[i].value = e.target.value;
        setPortGroups(p);
    };

    const handleDeleteAddress = (i) => {
        const a = [...addressGroups];
        a.splice(i, 1);
        setAddressGroups(a);
    };

    const handleDeletePort = (i) => {
        const p = [...portGroups];
        p.splice(i, 1);
        setPortGroups(p);
    };

    const handleModeChange = (m) => {
        setMode(m);
    };

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid container item xs={12} style={{ display: mode === 'push' ? 'none' : 'flex' }}>
                    <Grid container item xs={12} className={classes.justifySpace}>
                        <Grid item xs={12} className={classes.buttonGroup}>
                            {!MC2CHECK && policy.source === 'mc2' && <Box mb={1} className={classes.warningBox} display="inline-block">
                                You cannot modify or push a policy assigned by an MC2.
                            </Box>}
                            {!readonly && <Button
                                variant="contained"
                                color="secondary"
                                aria-label="revert"
                                onClick={handleRevertPolicy}
                                disabled={!MC2CHECK && policy.source === 'mc2'}
                            >Revert</Button>}
                            {varsPerms.admin && <Button
                                variant="contained"
                                color="primary"
                                aria-label="push"
                                onClick={maybePushPolicy}
                                disabled={!MC2CHECK && policy.source === 'mc2'}
                                className={clsx({
                                    [classes.pushAnimation]: !(!MC2CHECK && policy.source === 'mc2') && addressChanges || portChanges,
                                })}
                            >Push
                            </Button>}
                            <IconButton aria-label="info" onClick={handleInfo}><InfoIcon /></IconButton>
                        </Grid>
                    </Grid>
                </Grid>
                <Box py={2}>
                    <Box display={mode === 'push' ? 'block' : 'none'}>
                        {varsPerms.admin && <Push
                            pushClasses={classes.buttonRight}
                            policyName={params.policy}
                            policyType={'suricata_config'}
                            onDeploy={handleDeployPolicy}
                            closePush={() => { handleModeChange('suricata_config') }}
                            pushing={pushing}
                            permName={'SuricataVarsPolicy'}
                        />}
                    </Box>
                    <Box display={mode === 'suricata_config' ? 'block' : 'none'} width="100%">
                        <Grid item xs={12}>
                            <Paper className={classes.maxHeight}>
                                <Box p={2}>
                                    <Box mb={2}>
                                        <Typography variant="h5">Address Groups</Typography>
                                    </Box>
                                    <Grid container spacing={4}>
                                        {addressGroups.map((v, i) => {
                                            return (
                                                <Grid item xs={6} key={'suricata-address-field-' + i}>
                                                    <Paper elevation={3}>
                                                        <Box p={2} display="flex" justifyContent="space-between">
                                                            <Box width="38%"><TextField
                                                                variant="standard"
                                                                className={classes.varsInput}
                                                                disabled={readonly || !v.editable}
                                                                value={v.key}
                                                                onChange={(e) => { handleUpdateAddressKey(e, i) }} /></Box>
                                                            <Box width="60%"><TextField
                                                                variant="standard"
                                                                disabled={readonly}
                                                                className={classes.varsInput}
                                                                value={v.value}
                                                                onChange={(e) => { handleUpdateAddressValue(e, i) }}
                                                                InputProps={{
                                                                    endAdornment: <InputAdornment position="end">
                                                                        <Box display={v.editable && !readonly ? 'block' : 'none'}>
                                                                            <IconButton size="small" aria-label="delete" onClick={() => { handleDeleteAddress(i) }}><DeleteIcon /></IconButton>
                                                                        </Box>
                                                                    </InputAdornment>
                                                                }}
                                                            /></Box>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            );
                                        })}
                                        <Grid item xs={12} className={classes.buttonGroup}>
                                            {!readonly && <Button variant="contained" color="primary" aria-label="add address group" onClick={handleAddAddressGroup}><AddIcon /> Address Group</Button>}
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12}>
                            <Paper className={classes.maxHeight}>
                                <Box p={2}>
                                    <Box mb={2}>
                                        <Typography variant="h5">Port Groups</Typography>
                                    </Box>
                                    <Grid container spacing={4}>
                                        {portGroups.map((v, i) => {
                                            return (
                                                <Grid item xs={6} key={'suricata-address-field-' + i}>
                                                    <Paper elevation={3}>
                                                        <Box p={2} display="flex" justifyContent="space-between">
                                                            <Box width="38%"><TextField
                                                                variant="standard"
                                                                className={classes.varsInput}
                                                                disabled={readonly || !v.editable}
                                                                value={v.key}
                                                                onChange={(e) => { handleUpdatePortKey(e, i) }}
                                                                InputProps={{
                                                                    className: classes.inputFontSize,
                                                                }} /></Box>
                                                            <Box width="60%"><TextField
                                                                variant="standard"
                                                                disabled={readonly}
                                                                className={classes.varsInput}
                                                                value={v.value}
                                                                onChange={(e) => { handleUpdatePortValue(e, i) }}
                                                                InputProps={{
                                                                    className: classes.inputFontSize,
                                                                    endAdornment: <InputAdornment position="end">
                                                                        <Box display={v.editable && !readonly ? 'block' : 'none'}>
                                                                            <IconButton size="small" aria-label="delete" onClick={() => { handleDeletePort(i) }}><DeleteIcon /></IconButton>
                                                                        </Box>
                                                                    </InputAdornment>
                                                                }}
                                                            /></Box>
                                                        </Box>
                                                    </Paper>
                                                </Grid>
                                            );
                                        })}
                                        <Grid item xs={12} className={classes.buttonGroup}>
                                            {!readonly && <Button variant="contained" color="primary" aria-label="add port group" onClick={handleAddPortGroup}><AddIcon /> Port Group</Button>}
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Paper>
                        </Grid>
                    </Box>
                </Box>
            </Grid>
            {modified && <ConfirmationDialog
                open={!!modified}
                title="Add or remove variables"
                message={(() => {
                    return (
                        <Box>
                            {modified.added.length > 0 ? 'Added variables:' : ''}
                            <div style={{ paddingLeft: 25 }}>
                                {modified.added.map((v, i) => <div key={`a_${i}_${v}`}>{v}</div>)}
                            </div>

                            {modified.removed.length > 0 ? 'Removed variables:' : ''}
                            <div style={{ paddingLeft: 25 }}>
                                {modified.removed.map((v, i) => <div key={`r_${i}_${v}`}>{v}</div>)}
                            </div>
                            <br />
                            will be applied to this policy.
                        </Box>
                    );
                })()}
                cancelLabel="Cancel"
                okLabel="Yes"
                onClose={() => { setModified(undefined) }}
                onOK={() => {
                    setModified(undefined);
                    handlePushPolicy();
                }}
            />}
        </Box>
    );
};

export default SuricataVars;
